import type { ReactNode } from 'react';

/**
 * A labelled form field.
 *
 * The label is a real `<label for>` rather than styled text — a control room is operated quickly and
 * sometimes with a keyboard or a screen reader, and clicking the label to focus the input is
 * behaviour people expect.
 *
 * ⚠️ **The hint sits in a reserved slot, so a field with one lines up with a field without.**
 * Fields are laid out side by side, and a hint is an extra line of text at the BOTTOM of one of
 * them. Aligned by their bottoms, the field carrying a hint pushed its own label and control a line
 * higher than its neighbour — which is exactly what the filter row looked like: two labels at two
 * different heights and two controls at two others, on a card the eye reads as broken before it
 * reads a word of it. Aligning by the top fixes the controls; `min-h` on the hint keeps the row
 * from ending ragged.
 */
export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-theme-sm font-medium text-gray-700">
        {label}
      </label>

      {children}

      {hint ? <p className="text-theme-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}
